import { IDropDownValue, TypeEnum } from 'types'
import DCSComboBox from './DCSComboBox'
import { useEffect, useState, useImperativeHandle, forwardRef } from 'react'
import { fetchTypeData } from 'core/helpers'
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns'

export interface ITypeComboBoxRef {
    fetchData: () => Promise<Array<IDropDownValue>>;
}

interface IProps {
    label?: string
    placeholder?: string
    allowEntry?: boolean
    value?: string
    onFilterChange?: Function
    onChange?: Function
    defaultValue?: string
    id?: string
    typeEnum?: TypeEnum
    name?: string
    typeParameterId?: number
    disabled?: boolean
    data?: Array<IDropDownValue>
    style?: React.CSSProperties
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    otherTypeParams?: any
    defaultId?: number
    maxLength?: number
    wrapperStyle?: React.CSSProperties
    onLoad?: (data: Array<IDropDownValue>) => void
    [key: string]: any // eslint-disable-line @typescript-eslint/no-explicit-any
}

const TypeComboBox = forwardRef(({
    placeholder,
    label,
    value,
    allowEntry,
    onFilterChange,
    onChange,
    defaultValue,
    id,
    typeEnum,
    name,
    typeParameterId,
    disabled,
    data,
    maxLength,
    style,
    otherTypeParams,
    defaultId,
    wrapperStyle,
    onLoad,
    ...otherProps
}: IProps, ref) => {

    const [typeData, setTypeData] = useState<Array<IDropDownValue>>([])
    const [stringData, setStringData] = useState<Array<string>>([])
    const [loading, setLoading] = useState(false)

    const fetchData = async () => {
        setLoading(true)

        if (data) {
            setTypeData(data)
            setStringData(data.map(item => item.text))
            setLoading(false)
            return data
        }

        const fetchData = await fetchTypeData(typeEnum!, typeParameterId, otherTypeParams)
        setTypeData(fetchData)

        //If parent does not have the text value of the default item, allow them to pass in the id so we can lookup the text value
        if (defaultId) {
            const found = fetchData.find(x => x.id === defaultId)
            if (found) {
                defaultValue = found.text
                onChange && onChange(found)
            }

        }


        setStringData(fetchData.map(item => item.text))
        setLoading(false)
        return fetchData
    }

    useEffect(() => {
        (async () => {
            const data = await fetchData()
            if (onLoad) {
                onLoad(data)
            }
        }
        )()
    }, [typeParameterId, defaultId])

    const localOnChange = (event: ComboBoxChangeEvent) => {
        let found = typeData.find(item => item.text === event.value)

        if (allowEntry && !found && event.value)
            found = { text: event.value, id: 0 }

        if (event.value === '' || found && found.text?.trim() === '')
            found = undefined

        if (onChange)
            onChange(found)
    }

    const localOnFilterChange = (value: string) => {

        let found = typeData.find(item => item.text === value)

        if (allowEntry && !found && value)
            found = { text: value, id: 0 }

        if (value === '' || found && found.text?.trim() === '')
            found = undefined

        if (onFilterChange)
            onFilterChange(found)
    }

    useImperativeHandle(ref, () => ({
        fetchData: async () => {
            return await fetchData()
        },
    }))

    return (
        <div style={wrapperStyle}>
            <DCSComboBox
                name={name}
                data={stringData}
                id={id}
                label={label}
                value={value}
                defaultValue={defaultValue}
                allowEntry={allowEntry}
                placeholder={placeholder}
                onFilterChange={localOnFilterChange}
                onChange={localOnChange}
                loading={loading}
                disabled={loading || disabled}
                style={style}
                maxLength={maxLength}
                {...otherProps}
            />
        </div>
    )
})

export default TypeComboBox